/**
 * PartnerAccountTable Component
 * 협력사 출금제한 테이블
 */
import React from 'react';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';
import { SortConfig } from '@/shared/hooks/useTableSort';
import { PartnerListItem } from '../types/partnerAccounting.types';
import { formatDateToDisplay } from '@/shared/utils/formatter';
import { accountingUtils } from '../utils/accountingUtils';

const {
    formatNumber,
    getStatusBadge,
    getGradeBadgeClass,
    getStatusBadgeClass,
    getPartnerRowClassName,
    isPartnerAccountDisabled,
    handleSendMoney,
} = accountingUtils;

const TABLE_HEADERS = [
    { key: 'suppName', label: '협력사명', sortable: false },
    { key: 'statusCode', label: '운영상태', sortable: false },
    { key: 'suppLvlName', label: '등급', sortable: true },
    { key: 'suppId', label: '협력사아이디', sortable: false },
    { key: 'insDate', label: '등록일자', sortable: true },
    { key: 'endMngDay', label: '운영종료일자', sortable: true },
    { key: 'balAmt', label: '계좌잔액', sortable: true },
    { key: 'unpaidAmt', label: '미납금', sortable: true },
    { key: 'action', label: '계좌관리', sortable: false },
] as const;

interface PartnerAccountTableProps {
    partnerData: PartnerListItem[];
    handleWithdrawalLimit: (suppName: string, suppId: string, withdrawalYn: string | null, targetAction: 'ALLOW' | 'RESTRICT') => void;
    sortConfig: SortConfig;
    onSort: (columnKey: string) => void;
}

const PartnerAccountTable: React.FC<PartnerAccountTableProps> = ({
                                                                     partnerData,
                                                                     handleWithdrawalLimit,
                                                                     sortConfig,
                                                                     onSort,
                                                                 }) => {
    return (
        <div className="table-container">
            <table className="table sortable-table">
                <thead>
                <tr>
                    {TABLE_HEADERS.map((header) => {
                        const isSorted = sortConfig.columnKey === header.key;
                        const direction = isSorted ? sortConfig.direction : null;
                        return (
                            <th
                                key={header.key}
                                className={`${header.sortable ? 'sortable-header' : ''} ${isSorted ? 'sorted' : ''}`}
                                onClick={() => header.sortable && onSort(header.key)}
                            >
                                {header.label}
                                {header.sortable && <SortIcon direction={direction} />}
                            </th>
                        );
                    })}
                </tr>
                </thead>
                <tbody>
                {partnerData.length === 0 ? (
                    <tr>
                        <td colSpan={9} className="text-center">
                            등록된 협력사가 없습니다.
                        </td>
                    </tr>
                ) : (
                    partnerData.map((account) => {
                        const isTerminated = isPartnerAccountDisabled(account.statusCode);
                        return (
                            <tr key={account.suppId} className={getPartnerRowClassName(account.statusCode, account.balAmt)}>
                                <td>{account.suppName}</td>
                                <td>
                                        <span className={`badge ${getStatusBadgeClass(account.statusCode)}`}>
                                            {account.statusCodeName || getStatusBadge(account.statusCode)}
                                        </span>
                                </td>
                                <td>
                                    {account.suppLvlName ? (
                                        <span className={`badge ${getGradeBadgeClass(account.suppLvlName)}`}>
                                                {account.suppLvlName}
                                            </span>
                                    ) : '-'}
                                </td>
                                <td>{account.suppId}</td>
                                <td>{formatDateToDisplay(account.insDate)}</td>
                                <td>{formatDateToDisplay(account.endMngDay)}</td>
                                <td className="number-cell">
                                    {account.balAmt !== null && account.balAmt !== undefined ? formatNumber(account.balAmt) : ''}
                                </td>
                                <td className="number-cell">
                                    {account.unpaidAmt !== null && account.unpaidAmt !== undefined ? formatNumber(account.unpaidAmt) : ''}
                                </td>
                                <td>
                                    <div className="action-buttons">
                                        <button
                                            className={`btn btn-send ${isTerminated ? 'disabled' : ''}`}
                                            onClick={() => !isTerminated && handleSendMoney(account.suppName, '-', account.suppId)}
                                            disabled={isTerminated}
                                        >
                                            일차감
                                        </button>
                                        <button
                                            className={`btn btn-limit allow ${account.withdrawalYn === 'N' ? 'active' : 'inactive'}`}
                                            onClick={() => !isTerminated && (account.withdrawalYn === 'Y' || account.withdrawalYn === null || account.withdrawalYn === undefined) && handleWithdrawalLimit(account.suppName, account.suppId, account.withdrawalYn, 'ALLOW')}
                                            disabled={isTerminated || account.withdrawalYn === 'N'}
                                        >
                                            출금허용
                                        </button>
                                        <button
                                            className={`btn btn-limit restrict ${account.withdrawalYn === 'Y' ? 'active' : 'inactive'}`}
                                            onClick={() => !isTerminated && (account.withdrawalYn === 'N' || account.withdrawalYn === null || account.withdrawalYn === undefined) && handleWithdrawalLimit(account.suppName, account.suppId, account.withdrawalYn, 'RESTRICT')}
                                            disabled={isTerminated || account.withdrawalYn === 'Y'}
                                        >
                                            출금제한
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })
                )}
                </tbody>
            </table>
        </div>
    );
};

export default PartnerAccountTable;
